import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { EmptyState } from '../components/common/empty-state';
import { DateDisplay } from '../components/common/date-display';
import { useAuditLog } from '../lib/api/hooks/use-queries';
import { auditActionLabel } from '../lib/labels';

const PAGE_SIZE = 50;

function formatDetailValue(value: unknown): string {
  if (value === null) return '—';
  if (Array.isArray(value)) return `[${value.map(formatDetailValue).join(', ')}]`;
  if (typeof value === 'object') {
    return `{ ${Object.entries(value)
      .map(([key, nested]) => `${key} : ${formatDetailValue(nested)}`)
      .join(' · ')} }`;
  }
  if (typeof value === 'boolean') return value ? 'oui' : 'non';
  return String(value);
}

function formatDetails(details: string | null): string {
  if (details === null || details === '') return '—';
  try {
    const parsed = JSON.parse(details) as unknown;
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.entries(parsed)
        .map(([key, value]) => `${key} : ${formatDetailValue(value)}`)
        .join(' · ');
    }
    return formatDetailValue(parsed);
  } catch {
    return details;
  }
}

function AuditLogPage(): JSX.Element {
  const [page, setPage] = useState(0);
  const auditLog = useAuditLog({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });

  if (auditLog.isLoading) return <Skeleton className="h-48" />;

  const entries = auditLog.data?.entries ?? [];
  const total = auditLog.data?.total ?? 0;
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = page * PAGE_SIZE + entries.length;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Journal d’audit</h1>
        <p className="text-sm text-muted-foreground">
          Trace des actions qui modifient la comptabilité : classifications manuelles, clôtures,
          extournes, synchronisations.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {entries.length === 0 ? (
            <EmptyState title="Aucune action enregistrée" />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Acteur</TableHead>
                    <TableHead>Détails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="whitespace-nowrap">
                        <DateDisplay value={entry.timestamp} withTime />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {auditActionLabel(entry.action)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {entry.actorEmail ?? 'Système'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDetails(entry.details)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {total === 0 ? 'Aucune entrée' : `${rangeStart}–${rangeEnd} sur ${total}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0 || auditLog.isFetching}
                onClick={() => setPage(page - 1)}
              >
                Précédent
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={rangeEnd >= total || auditLog.isFetching}
                onClick={() => setPage(page + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/audit-log')({ component: AuditLogPage });
